import { createContext, useContext, type ReactElement, type ReactNode } from 'react';

import type { HttpClient } from '@/api/httpClient';

/**
 * Acces au client HTTP depuis les composants.
 *
 * React Admin injecte `dataProvider` et `authProvider`, mais rien pour les appels
 * qui ne sont ni l'un ni l'autre : les actions d'archivage, la detection Hydra, la
 * lecture du compte connecte. Un contexte explicite vaut mieux qu'un import de
 * singleton — c'est ce qui rend les composants testables sans monter toute
 * l'application.
 */

const HttpClientContext = createContext<HttpClient | null>(null);

export const HttpClientProvider = ({
  client,
  children,
}: {
  client: HttpClient;
  children: ReactNode;
}): ReactElement => <HttpClientContext.Provider value={client}>{children}</HttpClientContext.Provider>;

/**
 * @throws Error si le fournisseur est absent. Echouer bruyamment au montage vaut
 *         mieux qu'un `null` qui se propage jusqu'a un appel reseau silencieux.
 */
export const useHttpClient = (): HttpClient => {
  const client = useContext(HttpClientContext);

  if (client === null) {
    throw new Error('useHttpClient doit être utilisé à l’intérieur de <HttpClientProvider>.');
  }

  return client;
};
